import UnitPageShell from '../components/shared/UnitPageShell';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import authService from '../services/authService';
import { useAuth } from '../context/AuthContext';

/* ------------------------------------------------------------------ */
/*  Berechtigungsdefinitionen mit ausfuehrlicher Tooltip-Beschreibung  */
/* ------------------------------------------------------------------ */

type PermissionDef = {
  key: string;
  label: string;
  description: string;
  category: string;
};

const PERMISSION_OPTIONS: PermissionDef[] = [
  // ── Allgemein / Admin ──
  {
    key: 'access_admin',
    label: 'Adminbereich betreten',
    description: 'Erlaubt den Zugang zum gesamten Adminbereich der WebApp. Ohne dieses Recht sieht der Benutzer keinen Admin-Menuepunkt und kann keine Admin-Seiten aufrufen.',
    category: 'Allgemein'
  },
  {
    key: 'notify_new_user',
    label: 'Neue Registrierung benachrichtigen',
    description: 'Der Benutzer erhaelt eine WhatsApp-Benachrichtigung, sobald sich ein neuer User registriert. Ermoeglicht schnelle Freigabe neuer Konten.',
    category: 'Allgemein'
  },
  {
    key: 'manage_profile',
    label: 'Profil verwalten',
    description: 'Erlaubt dem Benutzer, sein eigenes Profil zu bearbeiten: Alias-Name, WhatsApp-Nummer, Passwort aendern, Profilbild hochladen, eigene Quiz-Statistiken einsehen.',
    category: 'Allgemein'
  },

  // ── Benutzerverwaltung ──
  {
    key: 'manage_users',
    label: 'Benutzerverwaltung',
    description: 'Voller Zugriff auf die Benutzerverwaltung: Benutzer anlegen, bearbeiten, aktivieren/deaktivieren, Rollen zuweisen, Passwoerter zuruecksetzen. Zentrale Admin-Funktion.',
    category: 'Verwaltung'
  },
  {
    key: 'manage_teams',
    label: 'Teamverwaltung',
    description: 'Erlaubt das Erstellen, Bearbeiten und Loeschen von Teams sowie die Zuweisung von Benutzern zu Teams. Teams koennen fuer Gruppenauswertungen und Zugangssteuerung genutzt werden.',
    category: 'Verwaltung'
  },
  {
    key: 'manage_updates',
    label: 'Updates verwalten',
    description: 'Erstellen und Verwalten von Update-/Changelog-Eintraegen, die Benutzern nach dem Login angezeigt werden. Ermoeglicht die Kommunikation von Neuigkeiten und Aenderungen.',
    category: 'Verwaltung'
  },

  // ── Quiz ──
  {
    key: 'manage_quiz',
    label: 'Quiz verwalten (Legacy)',
    description: 'Legacy-Berechtigung fuer die aeltere Quiz-Verwaltung. Wird durch "Quiz-Fragen verwalten" ersetzt. Nur noch fuer Rueckwaertskompatibilitaet vorhanden.',
    category: 'Quiz'
  },
  {
    key: 'manage_quiz_questions',
    label: 'Quiz-Fragen verwalten',
    description: 'Erlaubt das Erstellen, Bearbeiten und Loeschen von Quiz-Fragen und -Antworten sowie das Zuordnen zu Modulen. Volle Kontrolle ueber den Quiz-Fragenpool.',
    category: 'Quiz'
  },
  {
    key: 'answer_quizzes',
    label: 'Quiz beantworten',
    description: 'Berechtigt den Benutzer, an Quizzes teilzunehmen und Antworten abzugeben. Ohne dieses Recht ist die Quiz-Funktion nicht sichtbar.',
    category: 'Quiz'
  },

  // ── FAQ (m98_u1_f_a_q) ──
  {
    key: 'manage_faq',
    label: 'FAQ verwalten (Voll)',
    description: 'Volle Admin-Kontrolle ueber die FAQ-Seite: Fragen stellen, beantworten, Bilder hochladen. Umfasst automatisch alle FAQ-Unterrechte.',
    category: 'FAQ'
  },
  {
    key: 'view_faq',
    label: 'FAQ anzeigen',
    description: 'Erlaubt den Zugriff auf die FAQ-Seite und das Lesen aller Fragen und Antworten. Ohne dieses Recht ist die FAQ-Seite nicht sichtbar.',
    category: 'FAQ'
  },
  {
    key: 'ask_faq_question',
    label: 'Frage stellen',
    description: 'Erlaubt das Stellen neuer Fragen auf der FAQ-Seite. Benutzer koennen ihre Fragen mit optionalem Bild einreichen.',
    category: 'FAQ'
  },
  {
    key: 'answer_faq',
    label: 'Fragen beantworten',
    description: 'Erlaubt das Beantworten offener Fragen auf der FAQ-Seite. Der Antwortende wird mit seinem Alias als Admin-Antwort angezeigt.',
    category: 'FAQ'
  },
  {
    key: 'upload_faq_images',
    label: 'Bilder hochladen',
    description: 'Erlaubt das Hochladen von Bildern zusammen mit FAQ-Fragen. Unterstuetzte Formate: JPG, PNG, GIF, WebP.',
    category: 'FAQ'
  },

  // ── Dokumente (m98_u5_dokumente) ──
  {
    key: 'manage_documents',
    label: 'Dokumente verwalten (Voll)',
    description: 'Volle Admin-Kontrolle ueber die Dokumentenverwaltung: Hochladen, Bearbeiten, Loeschen von PDFs. Umfasst automatisch alle Dokumente-Unterrechte.',
    category: 'Dokumente'
  },
  {
    key: 'view_documents',
    label: 'Dokumente ansehen',
    description: 'Erlaubt das Anzeigen der Dokumentenliste und das Herunterladen von PDFs. Ohne dieses Recht ist der Dokumentenbereich nicht zugaenglich.',
    category: 'Dokumente'
  },
  {
    key: 'edit_documents',
    label: 'Dokumente bearbeiten',
    description: 'Erlaubt das Aendern von Titel und Beschreibung bestehender Dokumente. Ohne dieses Recht koennen Dokumentinformationen nicht aktualisiert werden.',
    category: 'Dokumente'
  },
  {
    key: 'delete_documents',
    label: 'Dokumente loeschen',
    description: 'Erlaubt das Loeschen von Dokumenten aus der Dokumentenverwaltung. Geloeschte Dokumente werden dauerhaft entfernt.',
    category: 'Dokumente'
  },

  // ── Vorschlaege & Feedback (m98_u2_vorschlaege) ──
  {
    key: 'manage_vorschlaege',
    label: 'Vorschlaege verwalten (Voll)',
    description: 'Volle Admin-Kontrolle ueber die Vorschlagsseite: Alle Vorschlaege einsehen, verwalten. Umfasst automatisch alle Vorschlags-Unterrechte.',
    category: 'Vorschlaege & Feedback'
  },
  {
    key: 'view_vorschlaege',
    label: 'Seite anzeigen',
    description: 'Erlaubt den Zugriff auf die Seite "Vorschlaege & Feedback". Ohne dieses Recht ist die Vorschlagsseite nicht erreichbar.',
    category: 'Vorschlaege & Feedback'
  },
  {
    key: 'submit_vorschlaege',
    label: 'Vorschlag einreichen',
    description: 'Erlaubt das Einreichen von Vorschlaegen und Feedback mit Bild und optionalem Kommentar. Bilder aus dem Feld koennen hochgeladen werden.',
    category: 'Vorschlaege & Feedback'
  },

  // ── Telefonliste ──
  {
    key: 'manage_phonebook',
    label: 'Telefonliste verwalten (Voll)',
    description: 'Volle Admin-Kontrolle ueber die Telefonliste: Kontakte hinzufuegen, bearbeiten, loeschen, Bilder hochladen, Excel importieren. Umfasst automatisch alle Einzelrechte der Telefonliste.',
    category: 'Telefonliste'
  },
  {
    key: 'view_phonebook',
    label: 'Telefonliste ansehen',
    description: 'Erlaubt die Anzeige der Telefonliste mit allen Kontakten, Telefonnummern und E-Mail-Adressen. Ohne dieses Recht ist die Telefonliste nicht sichtbar. Kontakte koennen als vCard heruntergeladen werden.',
    category: 'Telefonliste'
  },
  {
    key: 'edit_phonebook',
    label: 'Kontakte bearbeiten',
    description: 'Erlaubt das Bearbeiten bestehender Kontakte in der Telefonliste: Name, Telefonnummer, E-Mail-Adresse aendern. Neue Kontakte koennen hinzugefuegt und bestehende aktualisiert werden.',
    category: 'Telefonliste'
  },
  {
    key: 'delete_phonebook',
    label: 'Kontakte loeschen',
    description: 'Erlaubt das Loeschen von Kontakten und Ueberschriften aus der Telefonliste. Geloeschte Eintraege werden sofort entfernt und koennen nicht wiederhergestellt werden.',
    category: 'Telefonliste'
  },
  {
    key: 'upload_phonebook_images',
    label: 'Kontaktbilder hochladen',
    description: 'Erlaubt das Hochladen und Ersetzen von Profilbildern/Kontaktfotos fuer einzelne Eintraege in der Telefonliste. Unterstuetzte Formate: JPG, PNG, GIF, WebP (max. 5 MB).',
    category: 'Telefonliste'
  },

  // ── Dashboard ──
  {
    key: 'manage_dashboard',
    label: 'Dashboard verwalten',
    description: 'Konfiguration und Verwaltung des Admin-Dashboards: Widgets anordnen, Statistik-Ansichten einrichten, Schnellzugriffe definieren.',
    category: 'Sonstige Bereiche'
  },

  // ── Highscore ──
  {
    key: 'manage_highscore',
    label: 'Highscore verwalten',
    description: 'Volle Admin-Kontrolle ueber das Highscore-System: Eintraege bearbeiten, zuruecksetzen, Konfiguration aendern. Umfasst alle Highscore-Unterrechte.',
    category: 'Highscore'
  },
  {
    key: 'manage_highscore_bars',
    label: 'Balken deaktivieren',
    description: 'Erlaubt das Aktivieren/Deaktivieren einzelner Quiz-Balken im Highscore-Diagramm. Deaktivierte Balken werden bei ALLEN Benutzern ausgeblendet und die Positionen rutschen auf. Die entsprechenden Legenden-Eintraege werden ebenfalls ausgeblendet.',
    category: 'Highscore'
  },
  {
    key: 'view_highscore_public',
    label: 'Highscore ansehen (Top 5 + eigener)',
    description: 'Erlaubt die Ansicht der oeffentlichen Highscore-Tabelle: Top 5 Benutzer je Quiz sowie den eigenen Platz und Punktestand.',
    category: 'Highscore'
  },
  {
    key: 'view_highscore_aggregate',
    label: 'Highscore Uebersicht',
    description: 'Zeigt eine aggregierte Uebersicht aller Quizzes mit Durchschnittsnoten, Teilnehmerzahlen und Trendverlauf. Gut fuer Team-/Abteilungsleiter.',
    category: 'Highscore'
  },
  {
    key: 'manage_highscore_prefs',
    label: 'Highscore Einstellungen',
    description: 'Ermoeglicht die Konfiguration der Highscore-Anzeige: Titel aendern, einzelne Benutzer von der Rangliste ausschliessen, Darstellungsoptionen anpassen.',
    category: 'Highscore'
  },
  {
    key: 'view_highscore_admin_details',
    label: 'Highscore Admin-Details',
    description: 'Erweiterte Admin-Ansicht: Alle Benutzer mit Einzelergebnissen, Detail-Charts pro Quiz und Benutzer, Joker-Kontostand je Benutzer sowie Export-Moeglichkeit. Nur fuer Administratoren gedacht.',
    category: 'Highscore'
  },

  // ── Inhalte Legacy ──
  {
    key: 'edit_content',
    label: 'Inhalte bearbeiten (Legacy)',
    description: 'Aeltere globale Berechtigung fuer Inhaltsbearbeitung. Wird durch die modulspezifischen Rechte (edit_texts_m1 bis m6) ersetzt. Nur noch fuer Rueckwaertskompatibilitaet.',
    category: 'Inhalte (Legacy)'
  },
  {
    key: 'edit_texts',
    label: 'Texte bearbeiten (Legacy, global)',
    description: 'Legacy-Recht: Erlaubt das Bearbeiten aller Textbloecke in allen Modulen. Wurde durch die modulspezifischen Rechte (edit_texts_m1-m6) abgeloest.',
    category: 'Inhalte (Legacy)'
  },
  {
    key: 'upload_images',
    label: 'Bilder hochladen (Legacy, global)',
    description: 'Legacy-Recht: Erlaubt das Hochladen und Ersetzen von Bildern in allen Modulen. Wurde durch die modulspezifischen Rechte (upload_images_m1-m6) abgeloest.',
    category: 'Inhalte (Legacy)'
  },

  // ── Step-by-Step Anleitungen ──
  {
    key: 'manage_stepbystep',
    label: 'Step-by-Step verwalten (Voll)',
    description: 'Volle Admin-Kontrolle ueber alle Step-by-Step-Anleitungsseiten: Design-Modus, Bloecke hinzufuegen/loeschen/sortieren, Texte bearbeiten, Medien hochladen, Sichtbarkeit steuern. Umfasst automatisch alle Einzelrechte.',
    category: 'Step-by-Step Anleitungen'
  },
  {
    key: 'edit_stepbystep',
    label: 'Anleitungen bearbeiten',
    description: 'Erlaubt den Zugang zum Design-Modus der Step-by-Step-Seiten: Textbloecke bearbeiten, neue Bloecke hinzufuegen und loeschen, Bloecke verschieben, Seiteneinstellungen aendern (Titel, Akzentfarbe, Mindestpunktzahl).',
    category: 'Step-by-Step Anleitungen'
  },
  {
    key: 'upload_stepbystep_media',
    label: 'Medien hochladen',
    description: 'Erlaubt das Hochladen von Bildern (JPG, PNG, GIF), Videos (MP4, WebM) und PDF-Dokumenten in Step-by-Step-Anleitungsseiten. Bestehende Medien koennen ersetzt werden.',
    category: 'Step-by-Step Anleitungen'
  },
  {
    key: 'manage_stepbystep_visibility',
    label: 'Sichtbarkeit steuern',
    description: 'Erlaubt das Ein- und Ausblenden von Step-by-Step-Seiten im Navigationsmenue. Versteckte Seiten sind fuer normale Benutzer nicht sichtbar.',
    category: 'Step-by-Step Anleitungen'
  },

  // ── Modul M10 – Ueberdach ──
  // ── M10 Seite: Datentypen & BACnet ──
  {
    key: 'view_m10_datentypen',
    label: 'Seite anzeigen',
    description: 'Erlaubt den Zugriff auf die Seite "Datentypen & BACnet Objekte" (m10_u1_x1) ohne Mindestpunktzahl. Zeigt CoDeSys-Datentypen mit Bit-Belegung, Wertebereichen und BACnet-Objekte mit Properties und Services.',
    category: 'M10 – Datentypen & BACnet'
  },
  {
    key: 'edit_m10_datentypen_access',
    label: 'Mindestpunktzahl aendern',
    description: 'Erlaubt das Aendern der Mindestpunktzahl (RequiredLevel) auf der Seite "Datentypen & BACnet". Bestimmt, ab welchem Punktestand normale Benutzer die Seite sehen koennen.',
    category: 'M10 – Datentypen & BACnet'
  },

  // ── M10 Seite: SPS-Funktionen ──
  {
    key: 'view_m10_funktionen',
    label: 'Seite anzeigen',
    description: 'Erlaubt den Zugriff auf die Seite "SPS-Funktionen" (m10_u1_x2) ohne Mindestpunktzahl. Zeigt Funktionen wie ADD, SUB, TON, R_TRIG etc. in FBD- und Strukturierter-Text-Darstellung.',
    category: 'M10 – SPS-Funktionen'
  },
  {
    key: 'edit_m10_funktionen_access',
    label: 'Mindestpunktzahl aendern',
    description: 'Erlaubt das Aendern der Mindestpunktzahl (RequiredLevel) auf der Seite "SPS-Funktionen". Bestimmt, ab welchem Punktestand normale Benutzer die Seite sehen koennen.',
    category: 'M10 – SPS-Funktionen'
  },

  // ── M10 Seite: FUP-Editor ──
  {
    key: 'view_m10_fup',
    label: 'FUP-Editor oeffnen',
    description: 'Erlaubt den Zugriff auf den interaktiven FUP-Editor (m10_u2_x1) ohne Mindestpunktzahl. Der FUP-Editor ermoeglicht das visuelle Programmieren mit Funktionsbausteinen per Drag&Drop.',
    category: 'M10 – FUP-Editor'
  },
  {
    key: 'edit_m10_fup_access',
    label: 'Mindestpunktzahl aendern',
    description: 'Erlaubt das Aendern der Mindestpunktzahl (RequiredLevel) auf der FUP-Editor-Seite. Bestimmt, ab welchem Punktestand normale Benutzer den FUP-Editor erreichen koennen.',
    category: 'M10 – FUP-Editor'
  },
  {
    key: 'edit_m10_fup_blocks',
    label: 'Bloecke bearbeiten',
    description: 'Erlaubt das Hinzufuegen, Loeschen und Verbinden von Funktionsbausteinen (AND, OR, TON, etc.), Ein-/Ausgaengen und das Erstellen/Loeschen von Verbindungslinien im FUP-Editor.',
    category: 'M10 – FUP-Editor'
  },
  {
    key: 'edit_m10_fup_networks',
    label: 'Netzwerke verwalten',
    description: 'Erlaubt das Hinzufuegen neuer Netzwerke und das Zuruecksetzen der Zeichenflaeche (Canvas Reset) im FUP-Editor. Ohne dieses Recht kann nur mit bestehenden Netzwerken gearbeitet werden.',
    category: 'M10 – FUP-Editor'
  },
  {
    key: 'use_m10_fup_simulation',
    label: 'Simulation nutzen',
    description: 'Erlaubt das Starten und Stoppen der Echtzeit-Simulation im FUP-Editor. Mit dieser Berechtigung koennen Netzwerke live getestet und Signalverlaeufe beobachtet werden. Eingangswerte koennen waehrend der Simulation per Klick umgeschaltet werden.',
    category: 'M10 – FUP-Editor'
  },
  {
    key: 'save_m10_fup_projects',
    label: 'Projekte speichern/laden',
    description: 'Erlaubt das Speichern von FUP-Netzwerken als JSON-Datei und das Laden/Importieren gespeicherter Projekte. Ohne dieses Recht kann der Editor nur temporaer genutzt werden.',
    category: 'M10 – FUP-Editor'
  },
  {
    key: 'validate_m10_fup_tasks',
    label: 'Aufgaben pruefen',
    description: 'Erlaubt das Pruefen und Validieren von FUP-Aufgaben. Die Validierung ueberprueft, ob die erstellte Schaltung die gestellte Aufgabe korrekt loest, und gibt Feedback zu jedem Netzwerk.',
    category: 'M10 – FUP-Editor'
  },

  // ── M10 Seite: CAD-Schaltungseditor ──
  {
    key: 'view_m10_cad',
    label: 'CAD-Editor oeffnen',
    description: 'Erlaubt den Zugriff auf den CAD-Schaltungseditor (m10_u2_x2_CAD) ohne Mindestpunktzahl. Im Editor koennen elektrische Schaltungen mit typischen Schaltschrank-Komponenten aufgebaut werden.',
    category: 'M10 – CAD-Schaltungseditor'
  },
  {
    key: 'edit_m10_cad_access',
    label: 'Mindestpunktzahl aendern',
    description: 'Erlaubt das Aendern der Mindestpunktzahl (RequiredLevel) fuer den CAD-Schaltungseditor.',
    category: 'M10 – CAD-Schaltungseditor'
  },
  {
    key: 'edit_m10_cad_components',
    label: 'Komponenten bearbeiten',
    description: 'Erlaubt das Hinzufuegen, Loeschen und Umbenennen von Komponenten (Sicherungen, Schuetze, Relais, Taster, Schalter, Trafo, Lampen usw.).',
    category: 'M10 – CAD-Schaltungseditor'
  },
  {
    key: 'edit_m10_cad_connections',
    label: 'Verbindungen bearbeiten',
    description: 'Erlaubt das Erstellen und Loeschen von Verdrahtungen im CAD-Editor. Leitungen koennen per Klick bearbeitet werden.',
    category: 'M10 – CAD-Schaltungseditor'
  },
  {
    key: 'use_m10_cad_simulation',
    label: 'Simulation nutzen',
    description: 'Erlaubt das Starten/Stoppen der Simulation und das Umschalten von Schaltern, Tastern und Sensoren waehrend der Simulation.',
    category: 'M10 – CAD-Schaltungseditor'
  },
  {
    key: 'save_m10_cad_projects',
    label: 'Projekte speichern/laden',
    description: 'Erlaubt den Export und Import von CAD-Projekten als JSON-Dateien.',
    category: 'M10 – CAD-Schaltungseditor'
  },
  {
    key: 'validate_m10_cad_tasks',
    label: 'CAD-Aufgaben pruefen',
    description: 'Erlaubt das automatische Pruefen der CAD-Aufgaben inkl. Fehlerdialog mit Hinweisen zu fehlenden/fehlerhaften Verbindungen.',
    category: 'M10 – CAD-Schaltungseditor'
  },

  // ── Modul M1: Lueftungstechnik ──
  {
    key: 'edit_texts_m1',
    label: 'Texte bearbeiten (M1)',
    description: 'Erlaubt das Bearbeiten aller Textbloecke (EditableTextBlock) in Modul M1 (Lueftungstechnik). Umfasst alle Unterseiten von M1 wie Waermerueckgewinnung, Abluefter, Brandschutzklappen etc.',
    category: 'Modul M1 – Lueftungstechnik'
  },
  {
    key: 'upload_images_m1',
    label: 'Bilder hochladen (M1)',
    description: 'Erlaubt das Hochladen, Ersetzen und Loeschen von Bildern (ZoomableImage) in Modul M1. Deckt alle Fotos, Schemas und Diagramme in den M1-Unterseiten ab.',
    category: 'Modul M1 – Lueftungstechnik'
  },

  // ── Modul M2 ──
  {
    key: 'edit_texts_m2',
    label: 'Texte bearbeiten (M2)',
    description: 'Erlaubt das Bearbeiten aller Textbloecke in Modul M2. Betrifft saemtliche Unterseiten und Lerneinheiten dieses Moduls.',
    category: 'Modul M2'
  },
  {
    key: 'upload_images_m2',
    label: 'Bilder hochladen (M2)',
    description: 'Erlaubt das Hochladen und Ersetzen von Bildern in allen Unterseiten von Modul M2.',
    category: 'Modul M2'
  },

  // ── Modul M3 ──
  {
    key: 'edit_texts_m3',
    label: 'Texte bearbeiten (M3)',
    description: 'Erlaubt das Bearbeiten aller Textbloecke in Modul M3. Betrifft saemtliche Unterseiten und Lerneinheiten dieses Moduls.',
    category: 'Modul M3'
  },
  {
    key: 'upload_images_m3',
    label: 'Bilder hochladen (M3)',
    description: 'Erlaubt das Hochladen und Ersetzen von Bildern in allen Unterseiten von Modul M3.',
    category: 'Modul M3'
  },

  // ── Modul M4 ──
  {
    key: 'edit_texts_m4',
    label: 'Texte bearbeiten (M4)',
    description: 'Erlaubt das Bearbeiten aller Textbloecke in Modul M4. Betrifft saemtliche Unterseiten und Lerneinheiten dieses Moduls.',
    category: 'Modul M4'
  },
  {
    key: 'upload_images_m4',
    label: 'Bilder hochladen (M4)',
    description: 'Erlaubt das Hochladen und Ersetzen von Bildern in allen Unterseiten von Modul M4.',
    category: 'Modul M4'
  },

  // ── Modul M5 ──
  {
    key: 'edit_texts_m5',
    label: 'Texte bearbeiten (M5)',
    description: 'Erlaubt das Bearbeiten aller Textbloecke in Modul M5. Betrifft saemtliche Unterseiten und Lerneinheiten dieses Moduls.',
    category: 'Modul M5'
  },
  {
    key: 'upload_images_m5',
    label: 'Bilder hochladen (M5)',
    description: 'Erlaubt das Hochladen und Ersetzen von Bildern in allen Unterseiten von Modul M5.',
    category: 'Modul M5'
  },

  // ── Modul M6 ──
  {
    key: 'edit_texts_m6',
    label: 'Texte bearbeiten (M6)',
    description: 'Erlaubt das Bearbeiten aller Textbloecke in Modul M6. Betrifft saemtliche Unterseiten und Lerneinheiten dieses Moduls.',
    category: 'Modul M6'
  },
  {
    key: 'upload_images_m6',
    label: 'Bilder hochladen (M6)',
    description: 'Erlaubt das Hochladen und Ersetzen von Bildern in allen Unterseiten von Modul M6.',
    category: 'Modul M6'
  },

  // ── Medien-Uploads ──
  {
    key: 'upload_videos',
    label: 'Videos hochladen',
    description: 'Erlaubt das Hochladen von Video-Dateien in die Medienbibliothek. Videos koennen anschliessend in Modulseiten eingebettet werden.',
    category: 'Medien-Uploads'
  },
  {
    key: 'upload_pdfs',
    label: 'PDFs hochladen',
    description: 'Erlaubt das Hochladen von PDF-Dokumenten in die Medienbibliothek. PDFs koennen als Downloads in Modulseiten eingebunden werden.',
    category: 'Medien-Uploads'
  },

  // ── Rollen-Legende ──
  {
    key: 'view_roles_legend',
    label: 'Rollen-Legende lesen',
    description: 'Erlaubt die Anzeige der Rollen-Legende mit allen definierten Rollen und deren Berechtigungen. Nur lesender Zugriff, keine Aenderungen moeglich.',
    category: 'Rollenverwaltung'
  },
  {
    key: 'manage_roles_legend',
    label: 'Rollen-Legende bearbeiten',
    description: 'Erlaubt das Bearbeiten der Rollen-Legende: Rollen hinzufuegen, Beschreibungen aendern, Berechtigungszuordnungen anpassen. Erfordert zusaetzlich "Rollen-Legende lesen".',
    category: 'Rollenverwaltung'
  },
  {
    key: 'manage_theme_settings',
    label: 'Design/Theme verwalten',
    description: 'Erlaubt das Bearbeiten der globalen Design- und Theme-Farben fuer Light- und Dark-Mode: Texte, Hintergruende, Karten und Akzentfarben.',
    category: 'Design & Theme'
  },

  // ── M99 DIP-Schalter ──
  {
    key: 'view_dipschalter',
    label: 'DIP-Schalter Rechner',
    description: 'Erlaubt den Zugriff auf den DIP-Schalter Rechner (m99_u1). Konvertiert zwischen Dezimaladresse und DIP-Schalterstellung inkl. Potentiometer-Darstellung.',
    category: 'M99 – DIP-Schalter'
  },

  // ── M99 Taupunkt ──
  {
    key: 'view_taupunkt',
    label: 'Taupunktrechner',
    description: 'Erlaubt den Zugriff auf den Taupunktrechner (m99_u2). Berechnet Taupunkt, absolute Feuchte und Kondensatmenge mit HX-Diagramm-Visualisierung.',
    category: 'M99 – Taupunkt'
  },

  // ── M99 Volumenstrom ──
  {
    key: 'view_volumenstrom',
    label: 'Volumenstromrechner',
    description: 'Erlaubt den Zugriff auf die Volumenstromberechnung (m99_u3). Unterstuetzt Wirkdruckmessung, Anemometer und Messblende/Venturi.',
    category: 'M99 – Volumenstrom'
  },

  // ── M99 Temperaturen ──
  {
    key: 'view_temperaturen',
    label: 'Temperaturfuehler-Rechner',
    description: 'Erlaubt den Zugriff auf den Temperaturfuehler-Rechner (m99_u4). Berechnet den Widerstand passiver PT-, NI- und NTC-Fuehler bei gegebener Temperatur.',
    category: 'M99 – Temperaturen'
  },

  // ── M99 Kabelberechnung ──
  {
    key: 'view_kabelberechnung',
    label: 'Kabelrechner anzeigen',
    description: 'Erlaubt den Zugriff auf die Kabelberechnung (m99_u5). Berechnet Kabelquerschnitt, Strombelastbarkeit und Spannungsfall nach DIN VDE 0298-4.',
    category: 'M99 – Kabelberechnung'
  },
  {
    key: 'save_kabelberechnung',
    label: 'Berechnungen speichern',
    description: 'Erlaubt das lokale Speichern, Laden und Loeschen von Kabelberechnungen. Gespeicherte Berechnungen koennen spaeter wieder aufgerufen und verglichen werden.',
    category: 'M99 – Kabelberechnung'
  },

  // ── M99 Leistungsberechnung ──
  {
    key: 'view_leistungsberechnung',
    label: 'Leistungsrechner',
    description: 'Erlaubt den Zugriff auf die Leistungsberechnung (m99_u6). Berechnet Wirkleistung, Strom, Spannung, Blindleistung und Wirkungsgrad mit Leistungsdreieck-Visualisierung.',
    category: 'M99 – Leistungsberechnung'
  },

  // ── M99 Widerstand ──
  {
    key: 'view_widerstand',
    label: 'Widerstandsrechner',
    description: 'Erlaubt den Zugriff auf den Widerstandsrechner (m99_u7). Konvertiert zwischen Farbcode und Widerstandswert (3-6 Ringe) mit SVG-Visualisierung und E24-Reihen-Suche.',
    category: 'M99 – Widerstand'
  },

  // ── M99 Skalierer ──
  {
    key: 'view_skalierer',
    label: 'Skalierer',
    description: 'Erlaubt den Zugriff auf den Skalierer (m99_u8). Skaliert Werte zwischen X/Y-Achspunkten vorwaerts und rueckwaerts inklusive Begrenzung.',
    category: 'M99 – Skalierer'
  },

  // ── M99 Farb-Codex ──
  {
    key: 'view_farbcodex',
    label: 'Farb-Codex',
    description: 'Erlaubt den Zugriff auf den Farb-Codex (m99_u9). Konvertiert und mischt Farben zwischen Hex, RGB, HSL und RAL.',
    category: 'M99 – Farb-Codex'
  },

  // ── M99 Energie-Kosten ──
  {
    key: 'view_energiekosten',
    label: 'Energie-Kostenrechner',
    description: 'Erlaubt den Zugriff auf den Energie-Kostenrechner (m99_u10). Berechnet Leistung, Verbrauch und Kosten fuer Strom, Waerme und Kaelte.',
    category: 'M99 – Energie-Kosten'
  },

  // ── M99 Raum-Energie ──
  {
    key: 'view_energierechner',
    label: 'Raum-Energie Rechner',
    description: 'Erlaubt den Zugriff auf den Raum-Energie Rechner (m99_u11). Berechnet Heiz-/Kuehlenergie in alle Richtungen.',
    category: 'M99 – Raum-Energie'
  },

  // ── Startseite Extras ──
  {
    key: 'play_car_race_game',
    label: 'Auto-Rennspiel starten',
    description: 'Erlaubt auf der Startseite den Zugriff auf den Button zum Starten des Auto-Rennspiels. Ohne dieses Recht bleibt der Spiel-Button ausgeblendet.',
    category: 'Startseite – Extras'
  },
  {
    key: 'can_view_car_race_debug',
    label: 'Auto-Rennspiel: Debug-Infos',
    description: 'Erlaubt das Anzeigen der erweiterten Debug-Informationen (FPS, Fahrzeugstats, Position, Geschwindigkeit, Select Car 1-7) im Auto-Rennspiel. Das Debug-Panel ist klappbar und erscheint links oben im Spielbildschirm.',
    category: 'Startseite – Extras'
  }
];

const MODULE_TEXT_PERMISSIONS = ['edit_texts_m1', 'edit_texts_m2', 'edit_texts_m3', 'edit_texts_m4', 'edit_texts_m5', 'edit_texts_m6'];
const MODULE_IMAGE_PERMISSIONS = ['upload_images_m1', 'upload_images_m2', 'upload_images_m3', 'upload_images_m4', 'upload_images_m5', 'upload_images_m6'];
const MODULE_CONTENT_PERMISSIONS = [...MODULE_TEXT_PERMISSIONS, ...MODULE_IMAGE_PERMISSIONS];

const ALL_PERMISSION_KEYS = PERMISSION_OPTIONS.map((item) => item.key);

const ROLE_PERMISSION_DEFAULTS: Record<string, string[]> = {
  admin: ALL_PERMISSION_KEYS,
  light_admin: ['access_admin', 'manage_users', 'manage_teams', 'manage_updates', 'view_roles_legend'],
  user_admin: ['manage_users', 'view_roles_legend'],
  team_admin: ['manage_teams'],
  role_admin: ['manage_roles_legend', 'view_roles_legend'],
  updates_admin: ['manage_updates'],
  quiz_admin: ['manage_quiz_questions'],
  docs_admin: ['manage_documents', 'view_documents', 'edit_documents', 'delete_documents'],
  phonebook_admin: ['manage_phonebook', 'view_phonebook', 'edit_phonebook', 'delete_phonebook', 'upload_phonebook_images'],
  faq_admin: ['manage_faq', 'view_faq', 'ask_faq_question', 'answer_faq', 'upload_faq_images'],
  dashboard_admin: ['manage_dashboard'],
  highscore_admin: ['manage_highscore', 'manage_highscore_bars', 'view_highscore_public', 'view_highscore_aggregate', 'manage_highscore_prefs', 'view_highscore_admin_details'],
  content_admin: [...MODULE_CONTENT_PERMISSIONS, 'upload_videos', 'upload_pdfs', 'manage_stepbystep', 'edit_stepbystep', 'upload_stepbystep_media', 'manage_stepbystep_visibility', 'view_m10_datentypen', 'edit_m10_datentypen_access', 'view_m10_funktionen', 'edit_m10_funktionen_access', 'view_m10_fup', 'edit_m10_fup_access', 'edit_m10_fup_blocks', 'edit_m10_fup_networks', 'use_m10_fup_simulation', 'save_m10_fup_projects', 'validate_m10_fup_tasks', 'view_m10_cad', 'edit_m10_cad_access', 'edit_m10_cad_components', 'edit_m10_cad_connections', 'use_m10_cad_simulation', 'save_m10_cad_projects', 'validate_m10_cad_tasks', 'view_dipschalter', 'view_taupunkt', 'view_volumenstrom', 'view_temperaturen', 'view_kabelberechnung', 'save_kabelberechnung', 'view_leistungsberechnung', 'view_widerstand', 'view_skalierer', 'view_farbcodex', 'view_energiekosten', 'view_energierechner'],
  editor: [...MODULE_CONTENT_PERMISSIONS, 'edit_stepbystep', 'upload_stepbystep_media', 'view_m10_datentypen', 'view_m10_funktionen', 'view_m10_fup', 'edit_m10_fup_blocks', 'edit_m10_fup_networks', 'use_m10_fup_simulation', 'save_m10_fup_projects', 'validate_m10_fup_tasks', 'view_m10_cad', 'edit_m10_cad_components', 'edit_m10_cad_connections', 'use_m10_cad_simulation', 'save_m10_cad_projects', 'validate_m10_cad_tasks', 'view_dipschalter', 'view_taupunkt', 'view_volumenstrom', 'view_temperaturen', 'view_kabelberechnung', 'save_kabelberechnung', 'view_leistungsberechnung', 'view_widerstand', 'view_skalierer', 'view_farbcodex', 'view_energiekosten', 'view_energierechner'],
  creator: [...MODULE_CONTENT_PERMISSIONS, 'upload_videos', 'upload_pdfs', 'edit_stepbystep', 'upload_stepbystep_media', 'view_m10_datentypen', 'view_m10_funktionen', 'view_m10_fup', 'edit_m10_fup_blocks', 'edit_m10_fup_networks', 'use_m10_fup_simulation', 'save_m10_fup_projects', 'validate_m10_fup_tasks', 'view_m10_cad', 'edit_m10_cad_components', 'edit_m10_cad_connections', 'use_m10_cad_simulation', 'save_m10_cad_projects', 'validate_m10_cad_tasks', 'view_dipschalter', 'view_taupunkt', 'view_volumenstrom', 'view_temperaturen', 'view_kabelberechnung', 'save_kabelberechnung', 'view_leistungsberechnung', 'view_widerstand', 'view_skalierer', 'view_farbcodex', 'view_energiekosten', 'view_energierechner'],
  user: ['manage_profile', 'answer_quizzes', 'view_documents', 'view_highscore_public', 'view_highscore_aggregate', 'view_faq', 'ask_faq_question', 'upload_faq_images', 'view_vorschlaege', 'submit_vorschlaege', 'view_dipschalter', 'view_taupunkt', 'view_volumenstrom', 'view_temperaturen', 'view_kabelberechnung', 'save_kabelberechnung', 'view_leistungsberechnung', 'view_widerstand', 'view_skalierer', 'view_farbcodex', 'view_energiekosten', 'view_energierechner']
};

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'light_admin', label: 'Light Admin' },
  { value: 'user_admin', label: 'User Admin' },
  { value: 'team_admin', label: 'Team Admin' },
  { value: 'role_admin', label: 'Role Admin' },
  { value: 'updates_admin', label: 'Updates Admin' },
  { value: 'quiz_admin', label: 'Quiz Admin' },
  { value: 'docs_admin', label: 'Docs Admin' },
  { value: 'phonebook_admin', label: 'Phonebook Admin' },
  { value: 'faq_admin', label: 'FAQ Admin' },
  { value: 'dashboard_admin', label: 'Dashboard Admin' },
  { value: 'highscore_admin', label: 'Highscore Admin' },
  { value: 'content_admin', label: 'Content Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'creator', label: 'Creator' },
  { value: 'editor_1', label: 'Editor L1' },
  { value: 'editor_2', label: 'Editor L2' },
  { value: 'editor_3', label: 'Editor L3' },
  { value: 'editor_4', label: 'Editor L4' },
  { value: 'editor_5', label: 'Editor L5' },
  { value: 'editor_6', label: 'Editor L6' },
  { value: 'editor_7', label: 'Editor L7' },
  { value: 'editor_8', label: 'Editor L8' },
  { value: 'editor_9', label: 'Editor L9' },
  { value: 'editor_10', label: 'Editor L10' },
  { value: 'creator_1', label: 'Creator L1' },
  { value: 'creator_2', label: 'Creator L2' },
  { value: 'creator_3', label: 'Creator L3' },
  { value: 'creator_4', label: 'Creator L4' },
  { value: 'creator_5', label: 'Creator L5' },
  { value: 'creator_6', label: 'Creator L6' },
  { value: 'creator_7', label: 'Creator L7' },
  { value: 'creator_8', label: 'Creator L8' },
  { value: 'creator_9', label: 'Creator L9' },
  { value: 'creator_10', label: 'Creator L10' },
  { value: 'aussendienst', label: 'Aussendienst' },
  { value: 'inbetriebnehmer', label: 'Inbetriebnehmer' },
  { value: 'programmierer', label: 'Programmierer' },
  { value: 'cad', label: 'CAD' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'support', label: 'Support' },
  { value: 'qa', label: 'QA' },
  { value: 'user', label: 'User' }
];

type MessageState = {
  severity: 'success' | 'error' | 'info';
  text: string;
};

type LegendRole = {
  key: string;
  label: string;
  functions: string;
  notes?: string;
  permissions?: string[];
};

const normalizePermissions = (input: unknown): string[] => {
  if (!Array.isArray(input)) return [];
  return input.map((p) => String(p).trim().toLowerCase()).filter((p) => p.length > 0);
};

const getDefaultPermissionsForRole = (key: string): string[] => {
  const normalized = key.trim().toLowerCase();
  if (!normalized) return [];
  if (ROLE_PERMISSION_DEFAULTS[normalized]) return ROLE_PERMISSION_DEFAULTS[normalized];
  if (normalized.startsWith('editor_')) return [...MODULE_CONTENT_PERMISSIONS];
  if (normalized.startsWith('creator_')) return [...MODULE_CONTENT_PERMISSIONS, 'upload_videos', 'upload_pdfs'];
  return [];
};

const ensureLegendRoles = (roles: LegendRole[]): LegendRole[] => {
  const mapped = roles
    .map((role) => ({
      key: String(role.key || '').trim().toLowerCase(),
      label: String(role.label || role.key || '').trim(),
      functions: String(role.functions || '').trim(),
      notes: String(role.notes || '').trim(),
      permissions: normalizePermissions(role.permissions)
    }))
    .filter((role) => role.key.length > 0);

  const merged = [...mapped];
  ROLE_OPTIONS.forEach((opt) => {
    if (!merged.find((r) => r.key === opt.value)) {
      merged.push({ key: opt.value, label: opt.label, functions: '', notes: '', permissions: [] });
    }
  });

  merged.forEach((role) => {
    if (!role.permissions || role.permissions.length === 0) {
      role.permissions = getDefaultPermissionsForRole(role.key);
    }
  });

  merged.sort((a, b) => a.key.localeCompare(b.key));
  return merged;
};

const AdminRolesPage: React.FC = () => {
  const { token, canViewRolesLegend, canEditRolesLegend } = useAuth();
  const [rolesLegendLoading, setRolesLegendLoading] = useState(false);
  const [rolesLegendSaving, setRolesLegendSaving] = useState(false);
  const [rolesLegendError, setRolesLegendError] = useState<string | null>(null);
  const [rolesLegendMeta, setRolesLegendMeta] = useState<{ updatedAt?: string | null; updatedBy?: number | string | null } | null>(null);
  const [rolesLegendDraft, setRolesLegendDraft] = useState<LegendRole[]>([]);
  const [message, setMessage] = useState<MessageState | null>(null);
  const [selectedRoleKey, setSelectedRoleKey] = useState<string | null>(null);

  /* Index der aktuell ausgewaehlten Rolle im Draft-Array */
  const selectedRoleIndex = useMemo(
    () => (selectedRoleKey ? rolesLegendDraft.findIndex((r) => r.key === selectedRoleKey) : -1),
    [selectedRoleKey, rolesLegendDraft]
  );
  const selectedRole = selectedRoleIndex >= 0 ? rolesLegendDraft[selectedRoleIndex] : null;

  /* Berechtigungen gruppiert nach Kategorie fuer die Anzeige */
  const groupedPermissions = useMemo(() => {
    const groups: { category: string; items: PermissionDef[] }[] = [];
    const catMap = new Map<string, PermissionDef[]>();
    PERMISSION_OPTIONS.forEach((p) => {
      const list = catMap.get(p.category) || [];
      list.push(p);
      catMap.set(p.category, list);
    });
    catMap.forEach((items, category) => groups.push({ category, items }));
    return groups;
  }, []);

  /* Anzahl aktiver Rechte fuer die Badge-Anzeige */
  const activePermissionsCount = selectedRole?.permissions?.length ?? 0;
  const totalPermissionsCount = PERMISSION_OPTIONS.length;

  const loadRolesLegend = useCallback(async () => {
    if (!token || !canViewRolesLegend) return;
    setRolesLegendLoading(true);
    setRolesLegendError(null);
    try {
      const result = await authService.getRolesLegend(token);
      if (!result?.success) {
        throw new Error(result?.error || 'Rollen-Legende konnte nicht geladen werden.');
      }
      const legend = result.legend || {};
      const roles = ensureLegendRoles(Array.isArray(legend.roles) ? legend.roles : []);
      setRolesLegendDraft(roles);
      setRolesLegendMeta({ updatedAt: legend.updatedAt || null, updatedBy: legend.updatedBy ?? null });
    } catch (e: any) {
      setRolesLegendError(e?.message || 'Rollen-Legende konnte nicht geladen werden.');
    } finally {
      setRolesLegendLoading(false);
    }
  }, [token, canViewRolesLegend]);

  useEffect(() => {
    loadRolesLegend();
  }, [loadRolesLegend]);

  const handleLegendFieldChange = (index: number, field: 'key' | 'label' | 'functions' | 'notes', value: string) => {
    setRolesLegendDraft((prev) => prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item)));
  };

  const handleLegendPermissionToggle = (index: number, permission: string, checked: boolean) => {
    setRolesLegendDraft((prev) => prev.map((item, idx) => {
      if (idx !== index) return item;
      const current = Array.isArray(item.permissions) ? item.permissions : [];
      const next = checked
        ? Array.from(new Set([...current, permission]))
        : current.filter((perm) => perm !== permission);
      return { ...item, permissions: next };
    }));
  };

  const handleAddLegendRole = () => {
    setRolesLegendDraft((prev) => ([...prev, { key: '', label: '', functions: '', notes: '', permissions: [] }]));
  };

  const handleSaveRolesLegend = async () => {
    if (!token || !canEditRolesLegend) return;
    setRolesLegendSaving(true);
    setRolesLegendError(null);
    try {
      const payload = {
        version: 1,
        roles: rolesLegendDraft
      };
      const result = await authService.updateRolesLegend(token, payload);
      if (!result?.success) {
        throw new Error(result?.error || 'Rollen-Legende konnte nicht gespeichert werden.');
      }
      const legend = result.legend || {};
      const roles = ensureLegendRoles(Array.isArray(legend.roles) ? legend.roles : []);
      setRolesLegendDraft(roles);
      setRolesLegendMeta({ updatedAt: legend.updatedAt || null, updatedBy: legend.updatedBy ?? null });
      setMessage({ severity: 'success', text: 'Rollen-Legende gespeichert.' });
    } catch (e: any) {
      setRolesLegendError(e?.message || 'Rollen-Legende konnte nicht gespeichert werden.');
    } finally {
      setRolesLegendSaving(false);
    }
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!canViewRolesLegend) {
    return (
      <UnitPageShell title="Zugriff verweigert">
        <Container maxWidth={false} disableGutters sx={{ py: 6, px: { xs: 2, md: 4 } }}>
          <Alert severity="error">Keine Berechtigung fuer diese Seite.</Alert>
        </Container>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell
      title="Admin: Rollenverwaltung"
      actions={(
        <>
          <Button component={RouterLink} to="/admin" color="inherit">
            Zurueck
          </Button>
          <Button
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={loadRolesLegend}
            disabled={rolesLegendLoading}
          >
            Aktualisieren
          </Button>
        </>
      )}
      columns={{ xs: '1fr' }}
    >
      <Container maxWidth={false} disableGutters sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 } }}>
        <Paper sx={{ p: { xs: 2, md: 4 } }} elevation={1}>
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Waehle eine Rolle aus dem Dropdown, um deren Berechtigungen anzuzeigen und zu bearbeiten.
              Fahre mit der Maus ueber das Info-Symbol neben jedem Recht fuer eine ausfuehrliche Beschreibung.
            </Typography>
            {rolesLegendMeta?.updatedAt && (
              <Typography variant="caption" color="text.secondary">
                Letzte Aenderung: {rolesLegendMeta.updatedAt}
              </Typography>
            )}
            {message && (
              <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>
            )}
            {rolesLegendError && (
              <Alert severity="error">{rolesLegendError}</Alert>
            )}
            {rolesLegendLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={3}>
                {/* ── Rollen-Auswahl Dropdown ── */}
                <Autocomplete
                  options={rolesLegendDraft}
                  getOptionLabel={(role) => `${role.label || role.key} (${role.key})`}
                  value={rolesLegendDraft.find((r) => r.key === selectedRoleKey) || null}
                  onChange={(_e, newValue) => setSelectedRoleKey(newValue?.key ?? null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Rolle auswaehlen" placeholder="Tippe zum Suchen..." />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.key}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body1">{option.label || option.key}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.key}</Typography>
                      </Box>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option.key === value.key}
                  fullWidth
                  disableClearable={false}
                />

                {/* ── Inhalt fuer ausgewaehlte Rolle ── */}
                {selectedRole && selectedRoleIndex >= 0 && (
                  <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                    <Stack spacing={3}>
                      {/* Rollen-Metadaten */}
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Rollen-Key"
                          value={selectedRole.key}
                          onChange={(e) => handleLegendFieldChange(selectedRoleIndex, 'key', e.target.value)}
                          disabled={!canEditRolesLegend}
                          helperText="Technischer Schluessel der Rolle (z.B. admin, editor, user)"
                        />
                        <TextField
                          fullWidth
                          label="Anzeigename"
                          value={selectedRole.label}
                          onChange={(e) => handleLegendFieldChange(selectedRoleIndex, 'label', e.target.value)}
                          disabled={!canEditRolesLegend}
                          helperText="Sichtbarer Name der Rolle in der Oberflaeche"
                        />
                      </Box>

                      <Divider />

                      {/* Statistik-Chips + Buttons */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1 }}>
                            Berechtigungen
                          </Typography>
                          <Chip
                            size="small"
                            label={`${activePermissionsCount} / ${totalPermissionsCount} aktiv`}
                            color={activePermissionsCount > 0 ? 'primary' : 'default'}
                            variant="outlined"
                          />
                          {canEditRolesLegend && (
                            <>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  setRolesLegendDraft((prev) =>
                                    prev.map((r, i) =>
                                      i === selectedRoleIndex
                                        ? { ...r, permissions: ALL_PERMISSION_KEYS.slice() }
                                        : r
                                    )
                                  );
                                }}
                              >
                                Alle aktivieren
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                color="error"
                                onClick={() => {
                                  setRolesLegendDraft((prev) =>
                                    prev.map((r, i) =>
                                      i === selectedRoleIndex ? { ...r, permissions: [] } : r
                                    )
                                  );
                                }}
                              >
                                Alle deaktivieren
                              </Button>
                            </>
                          )}
                        </Box>
                        {canEditRolesLegend && (
                          <Button
                            variant="contained"
                            onClick={handleSaveRolesLegend}
                            disabled={rolesLegendSaving}
                            size="small"
                          >
                            {rolesLegendSaving ? 'Speichere ...' : 'Speichern'}
                          </Button>
                        )}
                      </Box>

                      {/* Gruppierte Berechtigungen als Accordion */}
                      {groupedPermissions.map((group, groupIndex) => (
                        <Accordion key={group.category} defaultExpanded={groupIndex === 0}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: 'primary.main'
                              }}
                            >
                              {group.category}
                            </Typography>
                            <Chip
                              size="small"
                              label={`${group.items.filter(item => selectedRole.permissions?.includes(item.key)).length}/${group.items.length}`}
                              sx={{ ml: 2 }}
                              variant="outlined"
                            />
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 0.5
                              }}
                            >
                              {group.items.map((perm) => {
                                const isActive = Boolean(selectedRole.permissions?.includes(perm.key));
                                return (
                                  <Box
                                    key={perm.key}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      py: 0.5,
                                      px: 1,
                                      borderRadius: 1,
                                      bgcolor: isActive ? 'action.selected' : 'transparent',
                                      transition: 'background-color 0.15s'
                                    }}
                                  >
                                    <FormControlLabel
                                      sx={{ flex: 1, mr: 0 }}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={isActive}
                                          onChange={(e) =>
                                            handleLegendPermissionToggle(selectedRoleIndex, perm.key, e.target.checked)
                                          }
                                          disabled={!canEditRolesLegend}
                                          icon={<CancelIcon fontSize="small" sx={{ opacity: 0.3 }} />}
                                          checkedIcon={<CheckCircleIcon fontSize="small" />}
                                        />
                                      }
                                      label={
                                        <Typography variant="body2" sx={{ fontWeight: isActive ? 600 : 400 }}>
                                          {perm.label}
                                        </Typography>
                                      }
                                    />
                                    <Tooltip
                                      title={
                                        <Box sx={{ p: 0.5 }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                            {perm.label}
                                          </Typography>
                                          <Typography variant="body2">{perm.description}</Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{ mt: 1, display: 'block', opacity: 0.7 }}
                                          >
                                            Key: {perm.key}
                                          </Typography>
                                        </Box>
                                      }
                                      arrow
                                      placement="right"
                                      enterDelay={200}
                                    >
                                      <InfoIcon
                                        fontSize="small"
                                        sx={{ color: 'text.secondary', cursor: 'help', ml: 0.5, opacity: 0.6 }}
                                      />
                                    </Tooltip>
                                  </Box>
                                );
                              })}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))}

                      <Divider />

                      {/* Funktionen & Notizen */}
                      <TextField
                        fullWidth
                        label="Funktionen"
                        value={selectedRole.functions}
                        onChange={(e) => handleLegendFieldChange(selectedRoleIndex, 'functions', e.target.value)}
                        disabled={!canEditRolesLegend}
                        multiline
                        minRows={2}
                        helperText="Freitext-Beschreibung der Aufgaben/Funktionen dieser Rolle"
                      />
                      <TextField
                        fullWidth
                        label="Notizen"
                        value={selectedRole.notes || ''}
                        onChange={(e) => handleLegendFieldChange(selectedRoleIndex, 'notes', e.target.value)}
                        disabled={!canEditRolesLegend}
                        multiline
                        minRows={2}
                        helperText="Interne Notizen, nur fuer Admins sichtbar"
                      />
                    </Stack>
                  </Paper>
                )}

                {/* Keine Rolle ausgewaehlt */}
                {!selectedRole && !rolesLegendLoading && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 200,
                      bgcolor: 'action.hover'
                    }}
                  >
                    <Typography variant="body1" color="text.secondary" textAlign="center">
                      Bitte eine Rolle aus dem Dropdown oben auswaehlen,<br />
                      um deren Berechtigungen anzuzeigen und zu bearbeiten.
                    </Typography>
                  </Paper>
                )}

                {/* Aktions-Buttons */}
                {canEditRolesLegend && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                    <Button variant="outlined" onClick={handleAddLegendRole}>
                      Neue Rolle hinzufuegen
                    </Button>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
        </Paper>
      </Container>
    </UnitPageShell>
  );
};

export default AdminRolesPage;
